import { Badge, Box, Card, Flex, HStack, IconButton, Text } from '@chakra-ui/react'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { LuBox, LuSquare } from 'react-icons/lu'
import { useKnowledgeGraph } from '~/components/knowledge-graph/hooks/useKnowledgeGraph'
import KnowledgeGraph from '~/components/knowledge-graph/KnowledgeGraph'
import KnowledgeGraph3D from '~/components/knowledge-graph/KnowledgeGraph3D'
import { PageHeader } from '~/components/shell'
import UNSLoader from '~/components/UNSLoader'

export const Route = createFileRoute('/_app/automotive/knowledge-graph')({
  component: AutomotiveKnowledgeGraphPage,
})

const wsAddress = import.meta.env.VITE_SHARED_WS_ADDRESS
const wsUsername = import.meta.env.VITE_WS_USERNAME
const wsPassword = import.meta.env.VITE_WS_PASSWORD

function AutomotiveKnowledgeGraphPage() {
  const containerRef = useRef<HTMLDivElement>(null)
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 })
  const [mode, setMode] = useState<'2d' | '3d'>('2d')

  const { graphData, isConnected, stats } = useKnowledgeGraph({
    wsAddress,
    username: wsUsername,
    password: wsPassword,
    topicFilter: 'ConnCorp/#',
    clientIdPrefix: 'stories_kg_automotive',
  })

  useEffect(() => {
    const el = containerRef.current
    if (!el) {
      return
    }
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry) {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        })
      }
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const isLoading = isConnected && graphData.nodes.length === 0

  return (
    <Flex direction="column" h="100%" gap={4}>
      <PageHeader
        title="Automotive - Knowledge Graph"
        subtitle="Real-time network visualization of the automotive Unified Namespace"
      />

      <HStack gap={4}>
        <Badge colorPalette={isConnected ? 'green' : 'red'} variant="subtle" px={2} py={1}>
          {isConnected ? 'Connected' : 'Disconnected'}
        </Badge>
        <Text fontSize="sm" color="fg.muted">
          {stats.nodeCount} nodes &middot; {stats.linkCount} edges &middot;{' '}
          {stats.messagesPerSecond} msg/s
        </Text>
        <HStack ml="auto" gap={1}>
          <IconButton
            aria-label="2D view"
            size="sm"
            variant={mode === '2d' ? 'solid' : 'outline'}
            onClick={() => setMode('2d')}
          >
            <LuSquare />
          </IconButton>
          <IconButton
            aria-label="3D view"
            size="sm"
            variant={mode === '3d' ? 'solid' : 'outline'}
            onClick={() => setMode('3d')}
          >
            <LuBox />
          </IconButton>
        </HStack>
      </HStack>

      <Card.Root flex={1} minH={0} overflow="hidden">
        <Box ref={containerRef} h="100%" w="100%">
          {isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" h="100%" minH="400px">
              <UNSLoader />
            </Box>
          ) : graphData.nodes.length > 0 ? (
            mode === '2d' ? (
              <KnowledgeGraph
                graphData={graphData}
                width={dimensions.width}
                height={dimensions.height}
              />
            ) : (
              <KnowledgeGraph3D
                graphData={graphData}
                width={dimensions.width}
                height={dimensions.height}
              />
            )
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" h="100%">
              <Text color="fg.muted">Connecting to MQTT broker...</Text>
            </Box>
          )}
        </Box>
      </Card.Root>
    </Flex>
  )
}
